/**
 * Dynamic Entity Hook Factory
 *
 * Creates React Query hooks at runtime for any entity based on its config.
 * Replaces per-entity hooks.ts files generated by the CRUD generator.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import type { AuthConfig, EntityConfig } from '../types/app-config';
import { getStoredAuthToken, clearStoredAuthToken } from '../auth/token-storage';
import { getLoginPath } from '../auth/auth-paths';

// =============================================================================
// API Response Types
// =============================================================================

export interface ListResponse {
  data: Record<string, unknown>[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  search?: string;
  [key: string]: unknown;
}

export type UnauthorizedInterceptor = (context: {
  auth?: AuthConfig;
  loginUrl: string;
}) => void;

let globalUnauthorizedInterceptor: UnauthorizedInterceptor | null = null;

export function setGlobalUnauthorizedInterceptor(interceptor: UnauthorizedInterceptor | null) {
  globalUnauthorizedInterceptor = interceptor;
}

function unauthorizedErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  const status = (error as { status?: unknown }).status;
  if (typeof status === 'number') return status;

  const responseStatus = (error as { response?: { status?: unknown } }).response?.status;
  if (typeof responseStatus === 'number') return responseStatus;

  return undefined;
}

class ApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function handleUnauthorized(auth?: AuthConfig) {
  const loginUrl = getLoginPath(auth);

  if (globalUnauthorizedInterceptor) {
    globalUnauthorizedInterceptor({ auth, loginUrl });
    return;
  }

  clearStoredAuthToken(auth);
  if (typeof window !== 'undefined' && window.location.pathname !== loginUrl) {
    window.location.assign(loginUrl);
  }
}

function interceptUnauthorizedError(error: unknown, auth?: AuthConfig) {
  if (unauthorizedErrorStatus(error) === 401) {
    handleUnauthorized(auth);
  }
}

async function withUnauthorizedInterception<T>(
  request: () => Promise<T>,
  auth?: AuthConfig,
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    interceptUnauthorizedError(error, auth);
    throw error;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

export function normalizeListResponse(response: unknown, fallbackLimit = 10): ListResponse {
  // Check for { success: false } error envelope
  if (
    isRecord(response)
    && 'success' in response
    && response.success === false
  ) {
    const message = typeof response.error === 'string'
      ? response.error
      : 'API request failed';
    throw new Error(message);
  }

  const fallback = {
    data: [],
    total: 0,
    page: 1,
    limit: fallbackLimit,
    totalPages: 1,
  };

  if (response === null || response === undefined) {
    throw new Error('Empty response body');
  }

  if (Array.isArray(response)) {
    const total = response.length;
    return {
      data: response as Record<string, unknown>[],
      total,
      page: 1,
      limit: Math.max(1, total || fallbackLimit),
      totalPages: 1,
    };
  }

  if (!isRecord(response)) {
    throw new Error('Invalid response format');
  }

  const nestedResult = isRecord(response.result) ? response.result : undefined;
  const directData = response.data;
  const nestedData = nestedResult?.data;
  const rows = Array.isArray(directData)
    ? directData
    : (Array.isArray(nestedData)
      ? nestedData
      : (Array.isArray(response.items)
        ? response.items
        : (Array.isArray(nestedResult?.items)
          ? nestedResult.items
          : undefined)));

  if (!rows) {
    return fallback;
  }

  const directPagination = isRecord(response.pagination) ? response.pagination : undefined;
  const nestedPagination = isRecord(nestedResult?.pagination) ? nestedResult.pagination : undefined;
  const directMeta = isRecord(response.meta) ? response.meta : undefined;
  const nestedMeta = isRecord(nestedResult?.meta) ? nestedResult.meta : undefined;

  const total = asNumber(response.total)
    ?? asNumber(response.totalCount)
    ?? asNumber(response.itemsCount)
    ?? asNumber(response.count)
    ?? asNumber(directPagination?.total)
    ?? asNumber(directPagination?.totalCount)
    ?? asNumber(directPagination?.itemsCount)
    ?? asNumber(directMeta?.total)
    ?? asNumber(directMeta?.totalCount)
    ?? asNumber(directMeta?.itemsCount)
    ?? asNumber(nestedResult?.total)
    ?? asNumber(nestedResult?.totalCount)
    ?? asNumber(nestedResult?.itemsCount)
    ?? asNumber(nestedResult?.count)
    ?? asNumber(nestedPagination?.total)
    ?? asNumber(nestedPagination?.totalCount)
    ?? asNumber(nestedPagination?.itemsCount)
    ?? asNumber(nestedMeta?.total)
    ?? asNumber(nestedMeta?.totalCount)
    ?? asNumber(nestedMeta?.itemsCount)
    ?? rows.length;
  const page = asNumber(response.page)
    ?? asNumber(response.currentPage)
    ?? asNumber(directPagination?.page)
    ?? asNumber(directPagination?.currentPage)
    ?? asNumber(directMeta?.page)
    ?? asNumber(directMeta?.currentPage)
    ?? asNumber(nestedResult?.page)
    ?? asNumber(nestedResult?.currentPage)
    ?? asNumber(nestedPagination?.page)
    ?? asNumber(nestedPagination?.currentPage)
    ?? asNumber(nestedMeta?.page)
    ?? asNumber(nestedMeta?.currentPage)
    ?? 1;
  const limit = asNumber(response.limit)
    ?? asNumber(response.pageSize)
    ?? asNumber(response.perPage)
    ?? asNumber(directPagination?.limit)
    ?? asNumber(directPagination?.pageSize)
    ?? asNumber(directPagination?.perPage)
    ?? asNumber(directMeta?.limit)
    ?? asNumber(directMeta?.pageSize)
    ?? asNumber(directMeta?.perPage)
    ?? asNumber(nestedResult?.limit)
    ?? asNumber(nestedResult?.pageSize)
    ?? asNumber(nestedResult?.perPage)
    ?? asNumber(nestedPagination?.limit)
    ?? asNumber(nestedPagination?.pageSize)
    ?? asNumber(nestedPagination?.perPage)
    ?? asNumber(nestedMeta?.limit)
    ?? asNumber(nestedMeta?.pageSize)
    ?? asNumber(nestedMeta?.perPage)
    ?? Math.max(1, rows.length || fallbackLimit);
  const totalPages = asNumber(response.totalPages)
    ?? asNumber(directPagination?.totalPages)
    ?? asNumber(directMeta?.totalPages)
    ?? asNumber(nestedResult?.totalPages)
    ?? asNumber(nestedPagination?.totalPages)
    ?? asNumber(nestedMeta?.totalPages)
    ?? Math.max(1, Math.ceil(total / Math.max(1, limit)));

  return {
    data: rows as Record<string, unknown>[],
    total,
    page,
    limit,
    totalPages,
  };
}

// =============================================================================
// Generic API Fetch
// =============================================================================

async function apiFetch<T>(
  baseUrl: string,
  path: string,
  auth: AuthConfig | undefined,
  options?: RequestInit & { params?: Record<string, unknown> }
): Promise<T> {
  let url = `${baseUrl}${path}`;

  if (options?.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });
    const queryString = searchParams.toString();
    if (queryString) url += `?${queryString}`;
  }

  const { params: _params, ...fetchOptions } = options || {};
  const headers = new Headers(fetchOptions.headers);
  const token = getStoredAuthToken(auth);

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (token) {
    const tokenHeader = auth?.tokenHeader || 'Authorization';
    if (tokenHeader.toLowerCase() === 'authorization') {
      headers.set(tokenHeader, token.startsWith('Bearer ') ? token : `Bearer ${token}`);
    } else {
      headers.set(tokenHeader, token);
    }
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new ApiError('Session expired. Please sign in again.', 401);
    }
    throw new ApiError(`API Error: ${response.status} ${response.statusText}`, response.status);
  }

  if (response.status === 204) return undefined as T;
  return response.json();
}

// =============================================================================
// Response Unwrapping
// =============================================================================

/**
 * Unwrap backend responses that follow the `{ success, data }` envelope pattern.
 *
 * The saas-kit backend wraps all single-entity responses as:
 *   { success: true, data: { id: "...", name: "..." } }
 *
 * List responses now support multiple backend envelope styles in
 * `normalizeListResponse`, while this helper focuses on detail-like payloads.
 *
 * This helper unwraps single-entity responses (where `data` is an object,
 * not an array) including deeply nested `{ result: { data: {...} } }` shapes
 * so that useDetail/useCreate/useUpdate receive the entity record directly.
 */
export function unwrapApiResponse<T>(response: unknown): T {
  // Check for { success: false } error envelope
  if (
    isRecord(response)
    && 'success' in response
    && response.success === false
  ) {
    const message = typeof response.error === 'string'
      ? response.error
      : 'API request failed';
    throw new Error(message);
  }

  // Deep unwrap: { result: { data: {...} } }
  if (
    isRecord(response)
    && isRecord(response.result)
    && 'data' in response.result
    && isRecord(response.result.data)
  ) {
    return response.result.data as T;
  }

  if (
    isRecord(response) &&
    'data' in response
  ) {
    const envelope = response;
    // Only unwrap when `data` is a non-array object (single-entity response).
    // List responses have `data` as an array — leave those alone.
    if (
      envelope.data &&
      typeof envelope.data === 'object' &&
      !Array.isArray(envelope.data)
    ) {
      return envelope.data as T;
    }
  }
  return response as T;
}

// =============================================================================
// Hook Factory
// =============================================================================

export interface EntityHooks {
  useList: (params?: ListParams) => ReturnType<typeof useQuery<ListResponse>>;
  useDetail: (id: string | undefined) => ReturnType<typeof useQuery<Record<string, unknown>>>;
  useCreate: () => ReturnType<typeof useMutation<Record<string, unknown>, Error, Record<string, unknown>>>;
  useUpdate: () => ReturnType<typeof useMutation<Record<string, unknown>, Error, { id: string; data: Record<string, unknown> }>>;
  useDelete: () => ReturnType<typeof useMutation<void, Error, string>>;
}

interface MutationSnapshotContext {
  previousLists: Array<[QueryKey, ListResponse | undefined]>;
  previousDetail?: Record<string, unknown>;
  optimisticId?: string;
}

function listSnapshots(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKeyPlural: string,
): Array<[QueryKey, ListResponse | undefined]> {
  return queryClient.getQueriesData<ListResponse>({ queryKey: [queryKeyPlural] });
}

function restoreListSnapshots(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshots: Array<[QueryKey, ListResponse | undefined]>,
) {
  snapshots.forEach(([key, value]) => {
    queryClient.setQueryData<ListResponse | undefined>(key, value);
  });
}

function canInsertOptimisticRow(listQueryKey: QueryKey): boolean {
  const params = Array.isArray(listQueryKey) && listQueryKey.length > 1
    ? listQueryKey[1]
    : undefined;

  if (!params || typeof params !== 'object') {
    return true;
  }

  const paramsRecord = params as Record<string, unknown>;
  const pageValue = paramsRecord.page;
  if (typeof pageValue === 'number' && pageValue > 1) {
    return false;
  }
  if (typeof pageValue === 'string' && Number(pageValue) > 1) {
    return false;
  }

  const searchValue = paramsRecord.search;
  if (typeof searchValue === 'string' && searchValue.trim()) {
    return false;
  }

  const structuralKeys = new Set(['page', 'limit', 'sort', 'order', 'search']);
  return !Object.entries(paramsRecord).some(([key, value]) => (
    !structuralKeys.has(key)
    && value !== undefined
    && value !== null
    && value !== ''
  ));
}

function recalculateTotalPages(total: number, limit: number): number {
  const safeLimit = Math.max(1, limit);
  return Math.max(1, Math.ceil(total / safeLimit));
}

/**
 * Creates a complete set of CRUD hooks for an entity at runtime.
 *
 * @example
 * ```tsx
 * const hooks = createEntityHooks(clientEntity, 'https://api.example.com');
 * const { data } = hooks.useList({ page: 1 });
 * ```
 */
export function createEntityHooks(
  entity: EntityConfig,
  apiBaseUrl: string,
  auth?: AuthConfig,
): EntityHooks {
  const queryKeyPlural = entity.slug;
  const queryKeySingular = entity.name.toLowerCase();

  return {
    useList(params?: ListParams) {
      return useQuery({
        queryKey: [queryKeyPlural, params],
        queryFn: () => withUnauthorizedInterception(async () => {
          const raw = await apiFetch<unknown>(apiBaseUrl, entity.apiPath, auth, {
            params: params as Record<string, unknown>,
          });
          return normalizeListResponse(raw, asNumber(params?.limit) ?? 10);
        }, auth),
      });
    },

    useDetail(id: string | undefined) {
      return useQuery({
        queryKey: [queryKeySingular, id],
        queryFn: () => withUnauthorizedInterception(async () => {
          const raw = await apiFetch<unknown>(apiBaseUrl, `${entity.apiPath}/${id}`, auth);
          return unwrapApiResponse<Record<string, unknown>>(raw);
        }, auth),
        enabled: !!id,
      });
    },

    useCreate() {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (data: Record<string, unknown>) => withUnauthorizedInterception(async () => {
          const raw = await apiFetch<unknown>(apiBaseUrl, entity.apiPath, auth, {
            method: 'POST',
            body: JSON.stringify(data),
          });
          return unwrapApiResponse<Record<string, unknown>>(raw);
        }, auth),
        onMutate: async (payload: Record<string, unknown>): Promise<MutationSnapshotContext> => {
          await queryClient.cancelQueries({ queryKey: [queryKeyPlural] });

          const previousLists = listSnapshots(queryClient, queryKeyPlural);
          const optimisticId = String(payload.id || `optimistic-${Date.now()}`);
          const optimisticRecord: Record<string, unknown> = {
            id: optimisticId,
            ...payload,
          };

          previousLists.forEach(([key, list]) => {
            if (!list || !canInsertOptimisticRow(key)) return;

            const limit = list.limit || list.data.length || 1;
            const nextTotal = list.total + 1;
            const nextData = [optimisticRecord, ...list.data].slice(0, limit);

            queryClient.setQueryData<ListResponse>(key, {
              ...list,
              data: nextData,
              total: nextTotal,
              totalPages: recalculateTotalPages(nextTotal, limit),
            });
          });

          return { previousLists, optimisticId };
        },
        onError: (_error, _payload, context) => {
          if (context) {
            restoreListSnapshots(queryClient, context.previousLists);
          }
        },
        onSuccess: (createdEntity, _payload, context) => {
          const createdId = String(createdEntity.id || context?.optimisticId || '');
          if (createdId) {
            queryClient.setQueryData<Record<string, unknown>>(
              [queryKeySingular, createdId],
              createdEntity,
            );
          }

          const snapshots = listSnapshots(queryClient, queryKeyPlural);
          snapshots.forEach(([key, list]) => {
            if (!list) return;

            const nextData = list.data.map(row => {
              if (String(row.id) === context?.optimisticId) {
                return createdEntity;
              }
              return row;
            });

            const hasCreatedRow = nextData.some(row => String(row.id) === createdId);
            if (!hasCreatedRow && canInsertOptimisticRow(key)) {
              const limit = list.limit || list.data.length || 1;
              nextData.unshift(createdEntity);
              nextData.splice(limit);
            }

            queryClient.setQueryData<ListResponse>(key, {
              ...list,
              data: nextData,
            });
          });
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [queryKeyPlural] });
        },
      });
    },

    useUpdate() {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => withUnauthorizedInterception(async () => {
          const raw = await apiFetch<unknown>(apiBaseUrl, `${entity.apiPath}/${id}`, auth, {
            method: 'PATCH',
            body: JSON.stringify(data),
          });
          return unwrapApiResponse<Record<string, unknown>>(raw);
        }, auth),
        onMutate: async ({ id, data }: { id: string; data: Record<string, unknown> }): Promise<MutationSnapshotContext> => {
          await queryClient.cancelQueries({ queryKey: [queryKeyPlural] });
          await queryClient.cancelQueries({ queryKey: [queryKeySingular, id] });

          const previousLists = listSnapshots(queryClient, queryKeyPlural);
          const previousDetail = queryClient.getQueryData<Record<string, unknown>>([queryKeySingular, id]);

          previousLists.forEach(([key, list]) => {
            if (!list) return;

            queryClient.setQueryData<ListResponse>(key, {
              ...list,
              data: list.data.map(row => (
                String(row.id) === id
                  ? { ...row, ...data }
                  : row
              )),
            });
          });

          if (previousDetail) {
            queryClient.setQueryData<Record<string, unknown>>(
              [queryKeySingular, id],
              { ...previousDetail, ...data },
            );
          }

          return { previousLists, previousDetail };
        },
        onError: (_error, { id }, context) => {
          if (!context) return;
          restoreListSnapshots(queryClient, context.previousLists);
          queryClient.setQueryData<Record<string, unknown> | undefined>(
            [queryKeySingular, id],
            context.previousDetail,
          );
        },
        onSuccess: (updatedEntity, { id }) => {
          queryClient.setQueryData<Record<string, unknown>>([queryKeySingular, id], updatedEntity);

          const snapshots = listSnapshots(queryClient, queryKeyPlural);
          snapshots.forEach(([key, list]) => {
            if (!list) return;
            queryClient.setQueryData<ListResponse>(key, {
              ...list,
              data: list.data.map(row => (
                String(row.id) === id
                  ? { ...row, ...updatedEntity }
                  : row
              )),
            });
          });
        },
        onSettled: (_updatedEntity, _error, { id }) => {
          queryClient.invalidateQueries({ queryKey: [queryKeySingular, id] });
          queryClient.invalidateQueries({ queryKey: [queryKeyPlural] });
        },
      });
    },

    useDelete() {
      const queryClient = useQueryClient();
      return useMutation({
        mutationFn: (id: string) => withUnauthorizedInterception(() =>
          apiFetch<void>(apiBaseUrl, `${entity.apiPath}/${id}`, auth, {
            method: 'DELETE',
          }), auth),
        onMutate: async (id: string): Promise<MutationSnapshotContext> => {
          await queryClient.cancelQueries({ queryKey: [queryKeyPlural] });
          await queryClient.cancelQueries({ queryKey: [queryKeySingular, id] });

          const previousLists = listSnapshots(queryClient, queryKeyPlural);
          const previousDetail = queryClient.getQueryData<Record<string, unknown>>([queryKeySingular, id]);

          previousLists.forEach(([key, list]) => {
            if (!list) return;

            const nextData = list.data.filter(row => String(row.id) !== id);
            const removed = nextData.length !== list.data.length;
            const nextTotal = removed ? Math.max(0, list.total - 1) : list.total;
            const limit = list.limit || list.data.length || 1;

            queryClient.setQueryData<ListResponse>(key, {
              ...list,
              data: nextData,
              total: nextTotal,
              totalPages: recalculateTotalPages(nextTotal, limit),
            });
          });

          queryClient.removeQueries({ queryKey: [queryKeySingular, id] });
          return { previousLists, previousDetail };
        },
        onError: (_error, id, context) => {
          if (!context) return;

          restoreListSnapshots(queryClient, context.previousLists);
          queryClient.setQueryData<Record<string, unknown> | undefined>(
            [queryKeySingular, id],
            context.previousDetail,
          );
        },
        onSuccess: (_response, id) => {
          queryClient.removeQueries({ queryKey: [queryKeySingular, id] });
        },
        onSettled: () => {
          queryClient.invalidateQueries({ queryKey: [queryKeyPlural] });
        },
      });
    },
  };
}
